// AccessCodeManager.jsx





// AccessCodeManager.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Button,
  TextField,
  CircularProgress,
  Snackbar,
  IconButton,
  Tooltip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import {
  fetchAccessCodes,
  createAccessCode,
  deleteAccessCode,
} from "../../services/SmartLocksService";

// import carousel organizer component (all new logic lives there)
import AccessCodeCarousel from "./AccessCodeCarousel";

// ✅ Helper: get current logged-in user's email from localStorage (set by useAuth)
const getLoggedInUserEmail = () => {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return (parsed?.email || "").trim();
  } catch {
    return "";
  }
};

// Helper function to extract address from lock name
const extractAddressFromLockName = (lockName) => {
  const match =
    lockName &&
    lockName?.match(
      /^(?:([\d\w\s.,#-]+),\s*([\w\s]+),\s*([A-Z]{2})\s*(\d{5})(?:\s*-.*)?|([\w\s-]+))$/i
    );

  // Address matched
  if (match && match[1]) {
    return `${match[1].trim()}, ${match[2].trim()}, ${match[3]
      .toUpperCase()} ${match[4]}`;
  }

  // Plain lock name matched (Igloo Tester)
  if (match && match[5]) {
    return match[5].trim();
  }
  return null;
};

// Function to generate a random access code name
const generateRandomCodeName = () => {
  const prefix = "Viewing-";
  const randomString = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `${prefix}${randomString}`;
};

/**
 * ✅ Display-only prettifier:
 * Turns:
 *   "Steven Smith-smith@aol.com-Viewing-OHOXkW"
 * into (multiline, labeled):
 *   "Name: Steven Smith
 *    Email: smith@aol.com
 *    Code: Viewing-OHOXkW"
 *
 * If name/email are missing, only shows what exists.
 */
const prettyAccessCodeName = (rawName) => {
  if (!rawName || typeof rawName !== "string") return rawName;

  const viewingIndex = rawName.indexOf("Viewing-");
  if (viewingIndex === -1) return rawName;

  const codePart = rawName.slice(viewingIndex).trim(); // "Viewing-OHOXkW"
  const prefixPart = rawName
    .slice(0, viewingIndex)
    .replace(/-+$/, "")
    .trim(); // "Steven Smith-smith@aol.com" (or "")

  // If it’s only the code part, keep it simple
  if (!prefixPart) return `Code: ${codePart}`;

  const parts = prefixPart.split("-").map((p) => p.trim()).filter(Boolean);
  const emailIdx = parts.findIndex((p) => p.includes("@"));
  const email = emailIdx >= 0 ? parts[emailIdx] : "";
  const name = parts
    .filter((_, idx) => idx !== emailIdx)
    .join("-")
    .trim();

  const lines = [];
  if (name) lines.push(`Name: ${name}`);
  if (email) lines.push(`Email: ${email}`);
  lines.push(`Code: ${codePart}`);

  return lines.join("\n");
};

/**
 * ✅ Applies display prettification without breaking the original structure.
 * We keep the original in `raw_access_code_name` (only if we rewrite).
 */
const formatCodesForDisplay = (codes) => {
  if (!Array.isArray(codes)) return [];

  return codes.map((c) => {
    const raw =
      c?.access_code_name ??
      c?.name ??
      c?.accessCodeName ??
      c?.access_code?.name ??
      c?.access_code?.display_name;

    if (!raw || typeof raw !== "string") return c;

    const pretty = prettyAccessCodeName(raw);

    // If nothing changed, return original object
    if (pretty === raw) return c;

    // Prefer rewriting access_code_name if it exists (most common),
    // otherwise rewrite name. Always keep raw in a safe field.
    if (typeof c?.access_code_name === "string") {
      return {
        ...c,
        raw_access_code_name: c.raw_access_code_name || raw,
        access_code_name: pretty,
      };
    }

    if (typeof c?.name === "string") {
      return {
        ...c,
        raw_access_code_name: c.raw_access_code_name || raw,
        name: pretty,
      };
    }

    // Fallback: add a display field only (in case carousel uses something else)
    return {
      ...c,
      raw_access_code_name: c.raw_access_code_name || raw,
      pretty_access_code_name: pretty,
    };
  });
};

const AccessCodeManager = ({
  isOpen,
  deviceType,
  onClose,
  uid,
  lockId,
  lockName, // <-- lock name is required for extraction
  initialEmail = "",
  initialPhone = "",
  initialStartsAt = "",
  initialEndsAt = "",
  initialAddress = "",
}) => {
  const [accessCodes, setAccessCodes] = useState([]);
  const [newCodeName, setNewCodeName] = useState("");

  // optional "identifier" name field (for access code name)
  const [contactName, setContactName] = useState("");

  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [address, setAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [extractError, setExtractError] = useState("");

  // dropdown state (accordion)
  const [codesExpanded, setCodesExpanded] = useState(false);

  // Load state on open/reset
  useEffect(() => {
    const loadAccessCodes = async () => {
      if (!uid || !lockId) return;
      setIsLoading(true);
      try {
        const codes = await fetchAccessCodes(uid, lockId);
        setAccessCodes(formatCodesForDisplay(codes));
      } catch (error) {
        alert("Failed to fetch access codes.");
        console.error("Error fetching access codes:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      loadAccessCodes();
      setNewCodeName(generateRandomCodeName());

      // reset optional name field on open
      setContactName("");

      setEmail(initialEmail);
      setPhoneNumber(initialPhone);

      // Parse initialStartsAt and initialEndsAt if provided
      if (initialStartsAt) {
        const startDateObj = new Date(initialStartsAt);
        setStartDate(startDateObj.toISOString().split("T")[0]);
        setStartTime(startDateObj.toTimeString().slice(0, 5)); // HH:MM format
      } else {
        setStartDate("");
        setStartTime("");
      }

      if (initialEndsAt) {
        const endDateObj = new Date(initialEndsAt);
        setEndDate(endDateObj.toISOString().split("T")[0]);
        setEndTime(endDateObj.toTimeString().slice(0, 5)); // HH:MM format
      } else {
        setEndDate("");
        setEndTime("");
      }

      setAddress(initialAddress);

      // collapse by default whenever opened
      setCodesExpanded(false);
    }
  }, [
    isOpen,
    uid,
    lockId,
    initialEmail,
    initialPhone,
    initialStartsAt,
    initialEndsAt,
    initialAddress,
  ]);

  // Button handler to extract and set address
  const handleExtractAddress = () => {
    if (!lockName) {
      setExtractError("No lock name available to extract address.");
      return;
    }
    const parsed = extractAddressFromLockName(lockName);
    if (parsed) {
      setAddress(parsed);
      setExtractError("");
    } else {
      setExtractError("Could not extract address from lock name.");
    }
  };

  const handleSendAccessCode = async (code) => {
    setIsSending(true);
    try {
      // Build payload with only provided values
      const payload = {
        access_code: code,
      };

      // Only include email if it's provided and not empty
      if (email && email.trim()) {
        payload.email = email.trim();
      }

      // Only include phone if it's provided and not empty
      if (phoneNumber && phoneNumber.trim()) {
        payload.phone = phoneNumber.trim();
      }

      // start_time and end_time are required - combine date and time
      if (!startDate || !startTime) {
        throw new Error("Start date and time are required to send access code.");
      }

      if (!endDate || !endTime) {
        throw new Error("End date and time are required to send access code.");
      }

      // Combine date and time for start
      const startDateTime = `${startDate}T${startTime}:00`;
      const startsAtDate = new Date(startDateTime);
      if (isNaN(startsAtDate.getTime())) {
        throw new Error("Invalid start date/time format.");
      }
      payload.start_time = startsAtDate.toISOString();

      // Combine date and time for end
      const endDateTime = `${endDate}T${endTime}:00`;
      const endsAtDate = new Date(endDateTime);
      if (isNaN(endsAtDate.getTime())) {
        throw new Error("Invalid end date/time format.");
      }
      payload.end_time = endsAtDate.toISOString();

      // Only include address if it's provided and not empty
      if (address && address.trim()) {
        payload.address = address.trim();
      }

      const endpoint =
        "https://ai-realtor.onrender.com/schedule-access-code-alerts";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Failed to send access code: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      await response.json();

      // ✅ Duplicate schedule: same payload, but email is swapped to logged-in Firebase user's email
      const loggedInEmail = getLoggedInUserEmail();
      const inputEmail = (email || "").trim();

      if (loggedInEmail && loggedInEmail !== inputEmail) {
        const dupPayload = {
          ...payload,
          email: loggedInEmail,
        };

        try {
          const dupResponse = await fetch(endpoint, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(dupPayload),
          });

          if (!dupResponse.ok) {
            let msg = `Duplicate schedule failed: ${dupResponse.statusText}`;
            try {
              const err = await dupResponse.json();
              msg = err?.error || msg;
            } catch {
              const t = await dupResponse.text();
              msg = t || msg;
            }
            console.warn(msg);
          } else {
            await dupResponse.json();
          }
        } catch (e) {
          console.warn("Duplicate schedule failed:", e);
        }
      }

      // Build success message based on what was sent
      const recipients = [];
      if (email && email.trim()) recipients.push(`email (${email.trim()})`);
      if (phoneNumber && phoneNumber.trim())
        recipients.push(`SMS (${phoneNumber.trim()})`);

      if (recipients.length > 0) {
        setSuccessMessage(
          `Access code ${code} sent to ${recipients.join(" and ")} successfully!`
        );
      } else {
        setSuccessMessage(`Access code ${code} created successfully!`);
      }
    } catch (error) {
      const errorMessage = error.message || "Failed to send access code.";
      alert(errorMessage);
      console.error("Error sending access code:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleCreateAccessCode = async () => {
    if (!uid || !lockId || !newCodeName) {
      alert("Please fill in all required fields.");
      return;
    }

    // Validate that dates and times are required
    if (!startDate || !endDate) {
      alert("Start date and expiration date are required.");
      return;
    }

    if (!startTime || !endTime) {
      alert("Start time and expiration time are required.");
      return;
    }

    // Combine date and time
    const startDateTime = `${startDate}T${startTime}:00`;
    const endDateTime = `${endDate}T${endTime}:00`;

    const startsAtDate = new Date(startDateTime);
    const endsAtDate = new Date(endDateTime);

    if (isNaN(startsAtDate.getTime()) || isNaN(endsAtDate.getTime())) {
      alert("Please enter valid start and expiration dates.");
      return;
    }

    if (startsAtDate >= endsAtDate) {
      alert("Start date/time must be before expiration date/time.");
      return;
    }

    // append optional name/email to the existing access code name
    // Example: "Steven Smith-smith@aol.com-Viewing-OHOXkW"
    const namePart = contactName && contactName.trim() ? contactName.trim() : "";
    const emailPart = email && email.trim() ? email.trim() : "";
    const finalCodeName = [namePart, emailPart, newCodeName]
      .filter(Boolean)
      .join("-");

    setIsLoading(true);
    try {
      const startsAtUTC = startsAtDate.toISOString();
      const endsAtUTC = endsAtDate.toISOString();
      const response = await createAccessCode(
        uid,
        lockId,
        finalCodeName,
        null,
        startsAtUTC,
        endsAtUTC,
        false,
        deviceType.includes("igloohome") ? true : false
      );

      const codes = await fetchAccessCodes(uid, lockId);
      setAccessCodes(formatCodesForDisplay(codes));

      const code = response.accessCode.access_code.code;

      // Automatically send if email or phoneNumber is present
      if (email || phoneNumber) {
        await handleSendAccessCode(code);
      } else {
        setSuccessMessage("Access code created successfully!");
      }
    } catch (error) {
      const errMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Unknown error";
      alert(`Failed to create access code: ${errMsg}`);
      console.error("Error creating access code:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccessCode = async (accessCodeId, deviceId) => {
    setIsLoading(true);
    try {
      await deleteAccessCode(uid, accessCodeId, deviceId);
      setAccessCodes((prevCodes) =>
        formatCodesForDisplay(
          prevCodes.filter((code) => code.access_code_id !== accessCodeId)
        )
      );
      setSuccessMessage("Access code deleted successfully!");
    } catch (error) {
      alert("Failed to delete access code.");
      console.error("Error deleting access code:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage("");
    setExtractError("");
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Manage Access Codes</DialogTitle>

      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Existing Access Codes:
        </Typography>

        {/* dropdown/accordion wrapper */}
        <Accordion
          expanded={codesExpanded}
          onChange={(_, expanded) => setCodesExpanded(expanded)}
          disableGutters
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            mb: 2,
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600 }}>
              {codesExpanded ? "Hide access codes" : "Show access codes"}{" "}
              {accessCodes?.length ? `(${accessCodes.length})` : ""}
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0 }}>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  py: 4,
                }}
              >
                <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
                <Typography>Loading access codes...</Typography>
              </Box>
            ) : accessCodes.length > 0 ? (
              // ✅ IMPORTANT:
              // We keep AccessCodeCarousel unchanged.
              // We pass multiline strings AND force Typography to respect \n via CSS targeting.
              <Box
                sx={{
                  "& .MuiTypography-root": {
                    whiteSpace: "pre-line",
                    lineHeight: 1.35,
                  },
                }}
              >
                <AccessCodeCarousel
                  accessCodes={accessCodes}
                  isLoading={isLoading}
                  onDelete={handleDeleteAccessCode}
                />
              </Box>
            ) : (
              <Typography>No access codes found for this lock.</Typography>
            )}
          </AccordionDetails>
        </Accordion>

        <Typography variant="subtitle1" sx={{ mt: 3 }}>
          Create a New Access Code:
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label="Access Code Name"
            variant="outlined"
            fullWidth
            value={newCodeName}
            onChange={(e) => setNewCodeName(e.target.value)}
            required
            disabled={isLoading || isSending}
          />

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <TextField
              label="Property Address"
              variant="outlined"
              fullWidth
              value={address}
              InputProps={{ readOnly: true }}
              helperText="Address of the property for this viewing"
              sx={{ flex: 1 }}
            />
            <Tooltip title="Paste address from lock name">
              <span>
                <IconButton
                  aria-label="Extract from lock name"
                  color="primary"
                  sx={{ ml: 1 }}
                  onClick={handleExtractAddress}
                  disabled={!lockName || isLoading || isSending}
                >
                  <ContentPasteGoIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <TextField
            label="Name (optional)"
            variant="outlined"
            fullWidth
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            helperText="Optional: used to label the access code name"
            disabled={isLoading || isSending}
          />

          <TextField
            label="Email (optional)"
            variant="outlined"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Enter email to send the access code (also appended to code name if provided)"
            disabled={isLoading || isSending}
          />

          <TextField
            label="Phone Number (optional)"
            variant="outlined"
            fullWidth
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            helperText="Enter phone number to send the access code"
            disabled={isLoading || isSending}
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              variant="outlined"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={isLoading || isSending}
              helperText="Required: Date when code becomes active"
            />
            <TextField
              label="Start Time"
              type="time"
              variant="outlined"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              disabled={isLoading || isSending}
              helperText="Required: Time when code becomes active"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Expiration Date"
              type="date"
              variant="outlined"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isLoading || isSending}
              helperText="Required: Date when code expires"
            />
            <TextField
              label="Expiration Time"
              type="time"
              variant="outlined"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              disabled={isLoading || isSending}
              helperText="Required: Time when code expires"
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleCreateAccessCode}
              disabled={isLoading || isSending}
            >
              {isLoading || isSending ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Create Access Code"
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isLoading || isSending}>
          Close
        </Button>
      </DialogActions>

      {/* Success & error snackbar */}
      <Snackbar
        open={!!successMessage || !!extractError}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {!!successMessage ? (
          <Alert severity="success" onClose={handleCloseSnackbar}>
            {successMessage}
          </Alert>
        ) : (
          <Alert severity="error" onClose={handleCloseSnackbar}>
            {extractError}
          </Alert>
        )}
      </Snackbar>
    </Dialog>
  );
};

export default AccessCodeManager;


// import React, { useState, useEffect } from "react";
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Typography,
//   Box,
//   Button,
//   TextField,
//   CircularProgress,
//   Snackbar,
//   IconButton,
//   Tooltip,
//   Alert,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
// } from "@mui/material";
// import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
// import {
//   fetchAccessCodes,
//   createAccessCode,
//   deleteAccessCode,
// } from "../../services/SmartLocksService";

// // import carousel organizer component (all new logic lives there)
// import AccessCodeCarousel from "./AccessCodeCarousel";

// // Helper function to extract address from lock name
// const extractAddressFromLockName = (lockName) => {
//   // Example: "123 Main St, New York, NY 10001 - Front Door"
//   const match =
//     lockName &&
//     lockName.match(
//       /^([\d\w\s\.,#-]+),\s*([\w\s]+),\s*([A-Z]{2})\s*(\d{5})(?:\s*-.*)?$/i
//     );
//   if (match) {
//     return `${match[1].trim()}, ${match[2].trim()}, ${match[3]
//       .trim()
//       .toUpperCase()} ${match[4].trim()}`;
//   }
//   return null;
// };

// // Function to generate a random access code name
// const generateRandomCodeName = () => {
//   const prefix = "Viewing-";
//   const randomString = Math.random().toString(36).substring(2, 8).toUpperCase();
//   return `${prefix}${randomString}`;
// };

// /**
//  * ✅ Display-only prettifier:
//  * Turns:
//  *   "Steven Smith-smith@aol.com-Viewing-OHOXkW"
//  * into (multiline, labeled):
//  *   "Name: Steven Smith
//  *    Email: smith@aol.com
//  *    Code: Viewing-OHOXkW"
//  *
//  * If name/email are missing, only shows what exists.
//  */
// const prettyAccessCodeName = (rawName) => {
//   if (!rawName || typeof rawName !== "string") return rawName;

//   const viewingIndex = rawName.indexOf("Viewing-");
//   if (viewingIndex === -1) return rawName;

//   const codePart = rawName.slice(viewingIndex).trim(); // "Viewing-OHOXkW"
//   const prefixPart = rawName
//     .slice(0, viewingIndex)
//     .replace(/-+$/, "")
//     .trim(); // "Steven Smith-smith@aol.com" (or "")

//   // If it’s only the code part, keep it simple
//   if (!prefixPart) return `Code: ${codePart}`;

//   const parts = prefixPart.split("-").map((p) => p.trim()).filter(Boolean);
//   const emailIdx = parts.findIndex((p) => p.includes("@"));
//   const email = emailIdx >= 0 ? parts[emailIdx] : "";
//   const name = parts
//     .filter((_, idx) => idx !== emailIdx)
//     .join("-")
//     .trim();

//   const lines = [];
//   if (name) lines.push(`Name: ${name}`);
//   if (email) lines.push(`Email: ${email}`);
//   lines.push(`Code: ${codePart}`);

//   return lines.join("\n");
// };

// /**
//  * ✅ Applies display prettification without breaking the original structure.
//  * We keep the original in `raw_access_code_name` (only if we rewrite).
//  */
// const formatCodesForDisplay = (codes) => {
//   if (!Array.isArray(codes)) return [];

//   return codes.map((c) => {
//     const raw =
//       c?.access_code_name ??
//       c?.name ??
//       c?.accessCodeName ??
//       c?.access_code?.name ??
//       c?.access_code?.display_name;

//     if (!raw || typeof raw !== "string") return c;

//     const pretty = prettyAccessCodeName(raw);

//     // If nothing changed, return original object
//     if (pretty === raw) return c;

//     // Prefer rewriting access_code_name if it exists (most common),
//     // otherwise rewrite name. Always keep raw in a safe field.
//     if (typeof c?.access_code_name === "string") {
//       return {
//         ...c,
//         raw_access_code_name: c.raw_access_code_name || raw,
//         access_code_name: pretty,
//       };
//     }

//     if (typeof c?.name === "string") {
//       return {
//         ...c,
//         raw_access_code_name: c.raw_access_code_name || raw,
//         name: pretty,
//       };
//     }

//     // Fallback: add a display field only (in case carousel uses something else)
//     return {
//       ...c,
//       raw_access_code_name: c.raw_access_code_name || raw,
//       pretty_access_code_name: pretty,
//     };
//   });
// };

// const AccessCodeManager = ({
//   isOpen,
//   onClose,
//   uid,
//   lockId,
//   lockName, // <-- lock name is required for extraction
//   initialEmail = "",
//   initialPhone = "",
//   initialStartsAt = "",
//   initialEndsAt = "",
//   initialAddress = "",
// }) => {
//   const [accessCodes, setAccessCodes] = useState([]);
//   const [newCodeName, setNewCodeName] = useState("");

//   // optional "identifier" name field (for access code name)
//   const [contactName, setContactName] = useState("");

//   const [email, setEmail] = useState("");
//   const [phoneNumber, setPhoneNumber] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [startTime, setStartTime] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [endTime, setEndTime] = useState("");
//   const [address, setAddress] = useState("");
//   const [isLoading, setIsLoading] = useState(false);
//   const [isSending, setIsSending] = useState(false);
//   const [generatedCode, setGeneratedCode] = useState(null);
//   const [successMessage, setSuccessMessage] = useState("");
//   const [extractError, setExtractError] = useState("");

//   // dropdown state (accordion)
//   const [codesExpanded, setCodesExpanded] = useState(false);

//   // Load state on open/reset
//   useEffect(() => {
//     const loadAccessCodes = async () => {
//       if (!uid || !lockId) return;
//       setIsLoading(true);
//       try {
//         const codes = await fetchAccessCodes(uid, lockId);
//         setAccessCodes(formatCodesForDisplay(codes));
//       } catch (error) {
//         alert("Failed to fetch access codes.");
//         console.error("Error fetching access codes:", error);
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     if (isOpen) {
//       loadAccessCodes();
//       setNewCodeName(generateRandomCodeName());

//       // reset optional name field on open
//       setContactName("");

//       setEmail(initialEmail);
//       setPhoneNumber(initialPhone);

//       // Parse initialStartsAt and initialEndsAt if provided
//       if (initialStartsAt) {
//         const startDateObj = new Date(initialStartsAt);
//         setStartDate(startDateObj.toISOString().split("T")[0]);
//         setStartTime(startDateObj.toTimeString().slice(0, 5)); // HH:MM format
//       } else {
//         setStartDate("");
//         setStartTime("");
//       }

//       if (initialEndsAt) {
//         const endDateObj = new Date(initialEndsAt);
//         setEndDate(endDateObj.toISOString().split("T")[0]);
//         setEndTime(endDateObj.toTimeString().slice(0, 5)); // HH:MM format
//       } else {
//         setEndDate("");
//         setEndTime("");
//       }

//       setAddress(initialAddress);
//       setGeneratedCode(null);

//       // collapse by default whenever opened
//       setCodesExpanded(false);
//     }
//   }, [
//     isOpen,
//     uid,
//     lockId,
//     initialEmail,
//     initialPhone,
//     initialStartsAt,
//     initialEndsAt,
//     initialAddress,
//   ]);

//   // Button handler to extract and set address
//   const handleExtractAddress = () => {
//     if (!lockName) {
//       setExtractError("No lock name available to extract address.");
//       return;
//     }
//     const parsed = extractAddressFromLockName(lockName);
//     if (parsed) {
//       setAddress(parsed);
//       setExtractError("");
//     } else {
//       setExtractError("Could not extract address from lock name.");
//     }
//   };

//   const handleSendAccessCode = async (code) => {
//     setIsSending(true);
//     try {
//       // Build payload with only provided values
//       const payload = {
//         access_code: code,
//       };

//       // Only include email if it's provided and not empty
//       if (email && email.trim()) {
//         payload.email = email.trim();
//       }

//       // Only include phone if it's provided and not empty
//       if (phoneNumber && phoneNumber.trim()) {
//         payload.phone = phoneNumber.trim();
//       }

//       // start_time and end_time are required - combine date and time
//       if (!startDate || !startTime) {
//         throw new Error("Start date and time are required to send access code.");
//       }

//       if (!endDate || !endTime) {
//         throw new Error("End date and time are required to send access code.");
//       }

//       // Combine date and time for start
//       const startDateTime = `${startDate}T${startTime}:00`;
//       const startsAtDate = new Date(startDateTime);
//       if (isNaN(startsAtDate.getTime())) {
//         throw new Error("Invalid start date/time format.");
//       }
//       payload.start_time = startsAtDate.toISOString();

//       // Combine date and time for end
//       const endDateTime = `${endDate}T${endTime}:00`;
//       const endsAtDate = new Date(endDateTime);
//       if (isNaN(endsAtDate.getTime())) {
//         throw new Error("Invalid end date/time format.");
//       }
//       payload.end_time = endsAtDate.toISOString();

//       // Only include address if it's provided and not empty
//       if (address && address.trim()) {
//         payload.address = address.trim();
//       }

//       const response = await fetch(
//         "https://ai-realtor.onrender.com/schedule-access-code-alerts",
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify(payload),
//         }
//       );

//       if (!response.ok) {
//         let errorMessage = `Failed to send access code: ${response.statusText}`;
//         try {
//           const errorData = await response.json();
//           errorMessage = errorData.error || errorMessage;
//         } catch {
//           const errorText = await response.text();
//           errorMessage = errorText || errorMessage;
//         }
//         throw new Error(errorMessage);
//       }

//       await response.json();

//       // Build success message based on what was sent
//       const recipients = [];
//       if (email && email.trim()) recipients.push(`email (${email.trim()})`);
//       if (phoneNumber && phoneNumber.trim())
//         recipients.push(`SMS (${phoneNumber.trim()})`);

//       if (recipients.length > 0) {
//         setSuccessMessage(
//           `Access code ${code} sent to ${recipients.join(" and ")} successfully!`
//         );
//       } else {
//         setSuccessMessage(`Access code ${code} created successfully!`);
//       }
//     } catch (error) {
//       const errorMessage = error.message || "Failed to send access code.";
//       alert(errorMessage);
//       console.error("Error sending access code:", error);
//     } finally {
//       setIsSending(false);
//     }
//   };

//   const handleCreateAccessCode = async () => {
//     if (!uid || !lockId || !newCodeName) {
//       alert("Please fill in all required fields.");
//       return;
//     }

//     // Validate that dates and times are required
//     if (!startDate || !endDate) {
//       alert("Start date and expiration date are required.");
//       return;
//     }

//     if (!startTime || !endTime) {
//       alert("Start time and expiration time are required.");
//       return;
//     }

//     // Combine date and time
//     const startDateTime = `${startDate}T${startTime}:00`;
//     const endDateTime = `${endDate}T${endTime}:00`;

//     const startsAtDate = new Date(startDateTime);
//     const endsAtDate = new Date(endDateTime);

//     if (isNaN(startsAtDate.getTime()) || isNaN(endsAtDate.getTime())) {
//       alert("Please enter valid start and expiration dates.");
//       return;
//     }

//     if (startsAtDate >= endsAtDate) {
//       alert("Start date/time must be before expiration date/time.");
//       return;
//     }

//     // append optional name/email to the existing access code name
//     // Example: "Steven Smith-smith@aol.com-Viewing-OHOXkW"
//     const namePart = contactName && contactName.trim() ? contactName.trim() : "";
//     const emailPart = email && email.trim() ? email.trim() : "";
//     const finalCodeName = [namePart, emailPart, newCodeName]
//       .filter(Boolean)
//       .join("-");

//     setIsLoading(true);
//     try {
//       const startsAtUTC = startsAtDate.toISOString();
//       const endsAtUTC = endsAtDate.toISOString();
//       const response = await createAccessCode(
//         uid,
//         lockId,
//         finalCodeName,
//         null,
//         startsAtUTC,
//         endsAtUTC
//       );

//       const codes = await fetchAccessCodes(uid, lockId);
//       setAccessCodes(formatCodesForDisplay(codes));

//       const code = response.accessCode.access_code.code;
//       setGeneratedCode(code);

//       // Automatically send if email or phoneNumber is present
//       if (email || phoneNumber) {
//         await handleSendAccessCode(code);
//       } else {
//         setSuccessMessage("Access code created successfully!");
//       }
//     } catch (error) {
//       alert(
//         `Failed to create access code: ${
//           error.error?.message || "Unknown error"
//         }`
//       );
//       console.error("Error creating access code:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleDeleteAccessCode = async (accessCodeId, deviceId) => {
//     setIsLoading(true);
//     try {
//       await deleteAccessCode(uid, accessCodeId, deviceId);
//       setAccessCodes((prevCodes) =>
//         formatCodesForDisplay(
//           prevCodes.filter((code) => code.access_code_id !== accessCodeId)
//         )
//       );
//       setSuccessMessage("Access code deleted successfully!");
//     } catch (error) {
//       alert("Failed to delete access code.");
//       console.error("Error deleting access code:", error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleCloseSnackbar = () => {
//     setSuccessMessage("");
//     setExtractError("");
//   };

//   return (
//     <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
//       <DialogTitle>Manage Access Codes</DialogTitle>

//       <DialogContent dividers>
//         <Typography variant="subtitle1" sx={{ mb: 2 }}>
//           Existing Access Codes:
//         </Typography>

//         {/* dropdown/accordion wrapper */}
//         <Accordion
//           expanded={codesExpanded}
//           onChange={(_, expanded) => setCodesExpanded(expanded)}
//           disableGutters
//           elevation={0}
//           sx={{
//             border: "1px solid",
//             borderColor: "divider",
//             borderRadius: 1,
//             mb: 2,
//             "&:before": { display: "none" },
//           }}
//         >
//           <AccordionSummary expandIcon={<ExpandMoreIcon />}>
//             <Typography sx={{ fontWeight: 600 }}>
//               {codesExpanded ? "Hide access codes" : "Show access codes"}{" "}
//               {accessCodes?.length ? `(${accessCodes.length})` : ""}
//             </Typography>
//           </AccordionSummary>

//           <AccordionDetails sx={{ pt: 0 }}>
//             {isLoading ? (
//               <Box
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   alignItems: "center",
//                   flexDirection: "column",
//                   py: 4,
//                 }}
//               >
//                 <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
//                 <Typography>Loading access codes...</Typography>
//               </Box>
//             ) : accessCodes.length > 0 ? (
//               // ✅ IMPORTANT:
//               // We keep AccessCodeCarousel unchanged.
//               // We pass multiline strings AND force Typography to respect \n via CSS targeting.
//               <Box
//                 sx={{
//                   "& .MuiTypography-root": {
//                     whiteSpace: "pre-line",
//                     lineHeight: 1.35,
//                   },
//                 }}
//               >
//                 <AccessCodeCarousel
//                   accessCodes={accessCodes}
//                   isLoading={isLoading}
//                   onDelete={handleDeleteAccessCode}
//                 />
//               </Box>
//             ) : (
//               <Typography>No access codes found for this lock.</Typography>
//             )}
//           </AccordionDetails>
//         </Accordion>

//         <Typography variant="subtitle1" sx={{ mt: 3 }}>
//           Create a New Access Code:
//         </Typography>

//         <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
//           <TextField
//             label="Access Code Name"
//             variant="outlined"
//             fullWidth
//             value={newCodeName}
//             onChange={(e) => setNewCodeName(e.target.value)}
//             required
//             disabled={isLoading || isSending}
//           />

//           <Box sx={{ display: "flex", alignItems: "center" }}>
//             <TextField
//               label="Property Address"
//               variant="outlined"
//               fullWidth
//               value={address}
//               InputProps={{ readOnly: true }}
//               helperText="Address of the property for this viewing"
//               sx={{ flex: 1 }}
//             />
//             <Tooltip title="Paste address from lock name">
//               <span>
//                 <IconButton
//                   aria-label="Extract from lock name"
//                   color="primary"
//                   sx={{ ml: 1 }}
//                   onClick={handleExtractAddress}
//                   disabled={!lockName || isLoading || isSending}
//                 >
//                   <ContentPasteGoIcon />
//                 </IconButton>
//               </span>
//             </Tooltip>
//           </Box>

//           <TextField
//             label="Name (optional)"
//             variant="outlined"
//             fullWidth
//             value={contactName}
//             onChange={(e) => setContactName(e.target.value)}
//             helperText="Optional: used to label the access code name"
//             disabled={isLoading || isSending}
//           />

//           <TextField
//             label="Email (optional)"
//             variant="outlined"
//             fullWidth
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//             helperText="Enter email to send the access code (also appended to code name if provided)"
//             disabled={isLoading || isSending}
//           />

//           <TextField
//             label="Phone Number (optional)"
//             variant="outlined"
//             fullWidth
//             value={phoneNumber}
//             onChange={(e) => setPhoneNumber(e.target.value)}
//             helperText="Enter phone number to send the access code"
//             disabled={isLoading || isSending}
//           />

//           <Box sx={{ display: "flex", gap: 2 }}>
//             <TextField
//               label="Start Date"
//               type="date"
//               variant="outlined"
//               fullWidth
//               required
//               InputLabelProps={{ shrink: true }}
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//               disabled={isLoading || isSending}
//               helperText="Required: Date when code becomes active"
//             />
//             <TextField
//               label="Start Time"
//               type="time"
//               variant="outlined"
//               fullWidth
//               required
//               InputLabelProps={{ shrink: true }}
//               value={startTime}
//               onChange={(e) => setStartTime(e.target.value)}
//               disabled={isLoading || isSending}
//               helperText="Required: Time when code becomes active"
//             />
//           </Box>

//           <Box sx={{ display: "flex", gap: 2 }}>
//             <TextField
//               label="Expiration Date"
//               type="date"
//               variant="outlined"
//               fullWidth
//               required
//               InputLabelProps={{ shrink: true }}
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//               disabled={isLoading || isSending}
//               helperText="Required: Date when code expires"
//             />
//             <TextField
//               label="Expiration Time"
//               type="time"
//               variant="outlined"
//               fullWidth
//               required
//               InputLabelProps={{ shrink: true }}
//               value={endTime}
//               onChange={(e) => setEndTime(e.target.value)}
//               disabled={isLoading || isSending}
//               helperText="Required: Time when code expires"
//             />
//           </Box>

//           <Box sx={{ display: "flex", gap: 2 }}>
//             <Button
//               variant="contained"
//               onClick={handleCreateAccessCode}
//               disabled={isLoading || isSending}
//             >
//               {isLoading || isSending ? (
//                 <CircularProgress size={24} color="inherit" />
//               ) : (
//                 "Create Access Code"
//               )}
//             </Button>
//           </Box>
//         </Box>
//       </DialogContent>

//       <DialogActions>
//         <Button onClick={onClose} disabled={isLoading || isSending}>
//           Close
//         </Button>
//       </DialogActions>

//       {/* Success & error snackbar */}
//       <Snackbar
//         open={!!successMessage || !!extractError}
//         autoHideDuration={6000}
//         onClose={handleCloseSnackbar}
//         anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
//       >
//         {!!successMessage ? (
//           <Alert severity="success" onClose={handleCloseSnackbar}>
//             {successMessage}
//           </Alert>
//         ) : (
//           <Alert severity="error" onClose={handleCloseSnackbar}>
//             {extractError}
//           </Alert>
//         )}
//       </Snackbar>
//     </Dialog>
//   );
// };

// export default AccessCodeManager;





